import {
  matchQuery,
  queryOptions,
  type Query,
  QueryClient,
} from '@tanstack/react-query';
import {
  API,
  BASE,
  V1,
  URL_AUTH,
  URL_BACKOFFICE,
  URL_ME,
} from '@/lib/constants/urls';
import { User } from '@/lib/auth/models';
import { HTTP_STATUS } from '@/api/common/http-status';

const AUTH_ME_URL = `${BASE}${API}${V1}${URL_BACKOFFICE}${URL_AUTH}${URL_ME}`;

export interface BackofficeAuthMeResponse {
  authenticated: boolean;
  user?: User;
}

/** `/me` di chi ha appena chiuso la sessione. */
export const SIGNED_OUT_ME: BackofficeAuthMeResponse = { authenticated: false };

const STALE_TIME = 30_000;
const GC_TIME = 300_000;

/**
 * Lettura di `/me` non riuscita per un motivo che non riguarda la sessione:
 * risposta 5xx (o comunque non 2xx, 401/403 esclusi) oppure errore di rete
 * (`status` `null`). Il router la riconosce e mostra la schermata con
 * «Riprova» e «Torna al login» invece dell'errore generico.
 */
export class AuthMeRequestError extends Error {
  readonly status: number | null;
  /** Errore originale della rete (il `TypeError` di `fetch`), se c'è. */
  readonly cause?: unknown;

  constructor(status: number | null, cause?: unknown) {
    super(
      status === null
        ? `${AUTH_ME_URL} failed: network error`
        : `${AUTH_ME_URL} failed with status ${status}`
    );
    this.name = 'AuthMeRequestError';
    this.status = status;
    this.cause = cause;
  }
}

export function isAuthMeRequestError(
  error: unknown
): error is AuthMeRequestError {
  return error instanceof AuthMeRequestError;
}

/**
 * Solo 401/403 dicono «sessione assente». Un 5xx o un errore di rete non
 * dicono nulla sulla sessione: la lettura fallisce (niente in cache) e il
 * guard la passa al router invece di mandare tutti al login.
 */
function isUnauthenticatedStatus(status: number): boolean {
  return (
    status === HTTP_STATUS.UNAUTHORIZED || status === HTTP_STATUS.FORBIDDEN
  );
}

export const authMeQueryOptions = queryOptions({
  queryKey: ['backoffice', 'auth', 'me'],
  queryFn: async (): Promise<BackofficeAuthMeResponse> => {
    const response = await fetch(AUTH_ME_URL, {
      method: 'GET',
      credentials: 'include',
      headers: { 'content-type': 'application/json;charset=UTF-8' },
    }).catch((cause: unknown) => {
      throw new AuthMeRequestError(null, cause);
    });

    if (isUnauthenticatedStatus(response.status)) {
      return { authenticated: false };
    }

    if (!response.ok) {
      throw new AuthMeRequestError(response.status);
    }

    return response.json();
  },
  staleTime: STALE_TIME,
  gcTime: GC_TIME,
  // Un 5xx non guarisce in un secondo: con il `retry` del client (più la
  // lettura del `RoleProvider`) `/me` partiva quattro volte prima di mostrare
  // l'errore. Si mostra subito la schermata con «Riprova».
  retry: false,
});

export async function ensureAuthData(
  queryClient: QueryClient
): Promise<BackofficeAuthMeResponse> {
  return queryClient.ensureQueryData(authMeQueryOptions);
}

function isAuthMeQuery(query: Query): boolean {
  return matchQuery({ queryKey: authMeQueryOptions.queryKey }, query);
}

/**
 * Svuota la cache all'uscita tenendo la sola voce di `/me` (già segnata
 * non autenticata): `RoleProvider` la osserva per tutta la vita dell'app e
 * il login successivo la riscrive. Un `clear()` la toglieva da sotto
 * l'osservatore, che non vedeva più il nuovo utente.
 */
export function clearCacheKeepingAuth(queryClient: QueryClient): void {
  queryClient.removeQueries({ predicate: (query) => !isAuthMeQuery(query) });
  queryClient.getMutationCache().clear();
}
